import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type AdrCatalogueResult,
  type CreateProductRequest,
  type EwcCatalogue,
  type EwcCatalogueSearchResult,
  type Product,
  type ProductFilters,
  type UpdateProductRequest,
} from '../client';

/** Stable query-key factory for product-scoped caches. */
export const productKeys = {
  all: ['directory', 'product'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'product', 'org', organisationId] as const,
  list: (organisationId: string, filters: ProductFilters) =>
    ['directory', 'product', 'org', organisationId, filters] as const,
};

/** Catalogue caches — the same for every tenant, so keyed by query only. */
export const dangerousGoodsCatalogueKeys = {
  adrSearch: (search: string, take: number) =>
    ['directory', 'adr-catalogue', 'search', search, take] as const,
  ewc: ['directory', 'ewc-catalogue'] as const,
  ewcSearch: (search: string, take: number) =>
    ['directory', 'ewc-catalogue', 'search', search, take] as const,
};

/**
 * The organisation's products, filtered server-side. Pass `null` while the
 * organisation is unknown. Keeps the previous page on screen while a new
 * filter loads, so typing in the search box does not flash the table empty.
 */
export function useProducts(
  organisationId: string | null,
  filters: ProductFilters = {},
  options?: Omit<UseQueryOptions<Product[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Product[], Error>({
    queryKey: organisationId ? productKeys.list(organisationId, filters) : productKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getProducts(organisationId, filters);
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** Mutation wrapper around POST `/products`. */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateProductRequest>({
    mutationKey: [...productKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createProduct(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: productKeys.byOrganisation(vars.organisationId) });
    },
  });
}

/**
 * Mutation wrapper around PUT `/products`. The caller passes `organisationId`
 * alongside the body so every filtered list of that organisation refreshes.
 */
export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; body: UpdateProductRequest }>({
    mutationKey: [...productKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateProduct(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({ queryKey: productKeys.byOrganisation(organisationId) });
    },
  });
}

/** Mutation wrapper around DELETE `/products/{id}`. */
export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...productKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteProduct(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: productKeys.byOrganisation(organisationId) });
    },
  });
}

/**
 * Search ADR Table A. Disabled until there is something to search for — the
 * empty query would list the table from the top, which a picker never wants.
 * The table only changes with a release, so results are cached for the day.
 */
export function useAdrCatalogueSearch(search: string, take = 30) {
  const q = search.trim();
  return useQuery<AdrCatalogueResult, Error>({
    queryKey: dangerousGoodsCatalogueKeys.adrSearch(q, take),
    queryFn: () => directoryApi.searchAdrCatalogue({ search: q, take }),
    enabled: q.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Search the EU List of Waste. Same shape and caching as the ADR search. */
export function useEwcCatalogueSearch(search: string, take = 30) {
  const q = search.trim();
  return useQuery<EwcCatalogueSearchResult, Error>({
    queryKey: dangerousGoodsCatalogueKeys.ewcSearch(q, take),
    queryFn: () => directoryApi.searchEwcCatalogue({ search: q, take }),
    enabled: q.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** The whole List of Waste, for a tree picker. Cached for the day. */
export function useEwcCatalogue() {
  return useQuery<EwcCatalogue, Error>({
    queryKey: dangerousGoodsCatalogueKeys.ewc,
    queryFn: () => directoryApi.getEwcCatalogue(),
    staleTime: 24 * 60 * 60 * 1000,
  });
}
